import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConversations } from '../../context/ConversationContext';
import { ROUTES } from '../../app/routes';

/**
 * Archive (one-way soft delete) a chat from any history list. If that chat is the
 * one currently open, leave it for a fresh chat — the same destinations the
 * sidebar's "New chat" uses: /chat → new chat, /channel/:guid/:id → the channel,
 * /amp/:ampId/:id → the AMP. Rejects with a user-facing message on failure.
 */
export function useArchiveChat(): (conversationId: string) => Promise<void> {
    const { archiveThread } = useConversations();
    const location = useLocation();
    const navigate = useNavigate();

    return useCallback(async (conversationId: string) => {
        await archiveThread(conversationId);

        const id = encodeURIComponent(conversationId);
        const path = location.pathname;
        if (path === `${ROUTES.CHAT}/${id}` || path === `${ROUTES.CHAT}/${conversationId}`) {
            // ChatContainer resets itself and moves to a new conversation on this event.
            window.dispatchEvent(new CustomEvent('newChatConversation'));
            return;
        }
        const channel = path.match(/^\/channel\/([^/]+)\/([^/]+)/);
        if (channel && decodeURIComponent(channel[2]) === conversationId) {
            const params = new URLSearchParams(location.search);
            const amp = params.get('amp');
            navigate(`/channel/${channel[1]}${amp ? `?amp=${encodeURIComponent(amp)}` : ''}`);
            return;
        }
        const amp = path.match(/^\/amp\/([^/]+)\/([^/]+)/);
        if (amp && decodeURIComponent(amp[2]) === conversationId) {
            navigate(`/amp/${amp[1]}`);
        }
    }, [archiveThread, location.pathname, location.search, navigate]);
}
